'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import usePushNotifications from '@/hooks/usePushNotifications';
import { initializeFirebase } from '@/lib/firebase/config';
import pushNotificationService from '@/lib/firebase/messaging';
import { toast } from 'react-toastify';

interface PushNotificationProviderProps {
  children: React.ReactNode;
  autoInitialize?: boolean;
}

/**
 * Provider component for Firebase push notifications
 * Initializes Firebase and manages push notification state
 */
export default function PushNotificationProvider({
  children,
  autoInitialize = true,
}: PushNotificationProviderProps) {
  const pathname = usePathname();
  const {
    token,
    isSupported,
    permission,
    isLoading,
    error,
    initialize,
    requestPermission,
  } = usePushNotifications();

  const initializedRef = useRef(false);
  const tokenDisplayedRef = useRef(false);

  // Check if we're on an auth page (login, signup, forgot-password, etc.)
  const isAuthPage = pathname && (
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/forgot-password' ||
    pathname.startsWith('/reset-password')
  );

  // Check if we're on the home page
  const isHomePage = pathname === '/' || pathname === '/home';

  // Initialize Firebase on mount and ensure service worker is ready
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Skip Firebase initialization on auth pages and home page
    if (isAuthPage) {
      console.log('[PushNotificationProvider] Skipping Firebase initialization on auth page:', pathname);
      return;
    }

    if (isHomePage) {
      console.log('[PushNotificationProvider] Skipping Firebase initialization on home page:', pathname);
      return;
    }

    const initFirebase = async () => {
      try {
        console.log('[PushNotificationProvider] Initializing Firebase...');
        await initializeFirebase();
        console.log('[PushNotificationProvider] ✅ Firebase initialized');
        
        // Ensure service worker is registered and has config after reload
        if ('serviceWorker' in navigator) {
          console.log('[PushNotificationProvider] Checking service worker status...');
          const registration = await navigator.serviceWorker.ready;
          
          if (registration.active) {
            console.log('[PushNotificationProvider] Service worker is active');
            // Re-inject config to service worker after page reload
            const { getFirebaseConfig } = await import('@/lib/firebase/config');
            const config = await getFirebaseConfig();
            
            console.log('[PushNotificationProvider] Sending Firebase config to service worker...', {
              hasApiKey: !!config.apiKey,
              hasProjectId: !!config.projectId,
            });
            
            registration.active.postMessage({
              type: 'FIREBASE_CONFIG',
              config: config,
            });
            console.log('[PushNotificationProvider] ✅ Service worker config refreshed after page load');
            
            // Also check if service worker is listening
            registration.active.postMessage({
              type: 'PING',
            });
          } else {
            console.warn('[PushNotificationProvider] ⚠️ Service worker is not active');
          }
        } else {
          console.warn('[PushNotificationProvider] ⚠️ Service workers not supported');
        }
      } catch (error) {
        console.error('[PushNotificationProvider] ❌ Failed to initialize Firebase:', error);
      }
    };

    initFirebase();
  }, [isAuthPage, pathname]);

  // Auto-initialize push notifications if enabled
  useEffect(() => {
    if (!autoInitialize || initializedRef.current) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Skip push notification initialization on auth pages
    if (isAuthPage) {
      console.log('[PushNotificationProvider] Skipping push notification initialization on auth page:', pathname);
      return;
    }

    // Initialize push notifications function
    const initializePushNotifications = async () => {
      // Check if supported - use direct service check instead of state
      const browserSupported = pushNotificationService.isSupported();
      
      if (!browserSupported) {
        console.warn('[PushNotificationProvider] Push notifications are not supported in this browser');
        // Run debug function to help diagnose
        if (typeof (window as any).debugPushNotifications === 'function') {
          (window as any).debugPushNotifications();
        }
        return;
      }

      // Check current permission status
      const currentPermission = pushNotificationService.getPermissionStatus();
      console.log('[PushNotificationProvider] Current permission status:', currentPermission);

      // Check permission status using current permission from service
      if (currentPermission === 'granted') {
        // Permission already granted, initialize
        console.log('[PushNotificationProvider] Permission already granted, initializing...');
        initialize().catch((error) => {
          console.error('[PushNotificationProvider] Failed to initialize push notifications:', error);
        });
        initializedRef.current = true;
      } else if (currentPermission === 'default') {
        // Permission not yet requested - initialize will request permission
        console.log('[PushNotificationProvider] Requesting notification permission...');
        initialize().catch((error) => {
          console.error('[PushNotificationProvider] Failed to initialize push notifications:', error);
        });
        initializedRef.current = true;
      } else if (currentPermission === 'denied') {
        console.warn('[PushNotificationProvider] Notification permission was denied by user');
        console.warn('[PushNotificationProvider] User must enable notifications in browser settings');
      } else if (currentPermission === null) {
        console.warn('[PushNotificationProvider] Cannot determine permission status');
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      const handleDOMReady = () => {
        if (document.readyState === 'complete') {
          initializePushNotifications();
        }
      };
      document.addEventListener('DOMContentLoaded', handleDOMReady);
      return () => {
        document.removeEventListener('DOMContentLoaded', handleDOMReady);
      };
    }

    // Initialize immediately if DOM is already ready
    initializePushNotifications();
  }, [autoInitialize, initialize, isAuthPage, pathname]);

  // Display token when available (after permission is granted)
  useEffect(() => {
    if (token && permission === 'granted' && !tokenDisplayedRef.current) {
      tokenDisplayedRef.current = true;
      
      console.log('[PushNotificationProvider] ✅ FCM Token obtained:', token);
      
      // Log full token to console for easy copying
      console.log('[PushNotificationProvider] 📋 Full FCM Token (copy this to send to backend):');
      console.log(token);
      console.log('---');
      
      // You can send this token to your backend to register the device
      // Example: sendTokenToBackend(token);
    }
    
    // Reset display flag if token becomes null (e.g., after refresh)
    if (!token) {
      tokenDisplayedRef.current = false;
    }
  }, [token, permission]);

  // Log errors
  useEffect(() => {
    if (error) {
      console.error('Push notification error:', error);
    }
  }, [error]);

  // Listen for messages from service worker (navigation and logging)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.warn('[PushNotificationProvider] Cannot setup message listener: window or serviceWorker not available');
      return;
    }

    console.log('[PushNotificationProvider] ✅ Setting up service worker message listener...');

    const handleMessage = async (event: Event | MessageEvent) => {
      console.log('[PushNotificationProvider] 📨 Message received from service worker:', event);
      
      // Type guard to check if it's a MessageEvent
      if (!('data' in event) || !event.data) {
        console.warn('[PushNotificationProvider] Message event missing data:', event);
        return;
      }
      
      const messageEvent = event as MessageEvent;
      console.log('[PushNotificationProvider] Message data:', messageEvent.data);

      // Handle navigation to notification URL
      if (messageEvent.data.type === 'NAVIGATE_TO_NOTIFICATION') {
        const url = messageEvent.data.url || '/';
        console.log('[PushNotificationProvider] Navigating to notification URL:', url);
        
        // Use Next.js router if available, otherwise use window.location
        if (typeof window !== 'undefined') {
          window.location.href = url;
        }
      }
      
      // Handle notification logging requests from service worker
      if (messageEvent.data.type === 'LOG_NOTIFICATION') {
        console.log('[PushNotificationProvider] 📬 LOG_NOTIFICATION received from service worker');
        const notification = messageEvent.data.notification;
        const title = notification?.title || 'New Notification';
        const body = notification?.body || '';
        const messageId = notification?.messageId || 'unknown';
        
        console.log('[PushNotificationProvider] Notification details:', {
          title,
          body,
          messageId,
          notification: notification,
        });
        
        // Track displayed toasts to prevent duplicates
        if (typeof window !== 'undefined') {
          if (!(window as any).__displayedNotificationIds) {
            (window as any).__displayedNotificationIds = new Set<string>();
          }
          
          // Check if this notification was already displayed
          if ((window as any).__displayedNotificationIds.has(messageId)) {
            console.log('[PushNotificationProvider] Skipping toast - notification already displayed:', messageId);
            // Still log the notification
            try {
              const { logNotification } = await import('@/lib/firebase/notificationLogger');
              const notificationType = notification.type === 'push' ? 'background' : (notification.type === 'foreground' ? 'foreground' : 'background');
              await logNotification({
                ...notification,
                notificationType: notificationType as 'foreground' | 'background',
                receivedAt: new Date(notification.receivedAt),
              });
            } catch (error) {
              console.error('[PushNotificationProvider] Failed to log notification:', error);
            }
            return;
          }
          
          // Mark this notification as displayed
          (window as any).__displayedNotificationIds.add(messageId);
          
          // Clean up old IDs after 1 hour to prevent memory leaks
          setTimeout(() => {
            if ((window as any).__displayedNotificationIds) {
              (window as any).__displayedNotificationIds.delete(messageId);
            }
          }, 60 * 60 * 1000); // 1 hour
        }
        
        // Display toast notification for all messages from service worker
        // Deduplication via __displayedNotificationIds will prevent duplicates with foreground handler
        // This ensures toast is displayed even if foreground handler (onMessage) doesn't fire
        // Track push notification toasts BEFORE any API calls to prevent dismissal
        if (typeof window !== 'undefined') {
          if (!(window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds = new Set<string>();
          }
          // Track that a push notification toast will be active (use messageId as identifier)
          (window as any).__pushNotificationToastIds.add(messageId);
          console.log('[PushNotificationProvider] Tracked push notification toast (before display):', messageId);
        }

        // Use requestAnimationFrame + setTimeout to ensure ToastContainer is ready
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              const notificationUrl = notification?.data?.url || notification?.data?.click_action || '/';
              
              // Check if ToastContainer exists in DOM
              const toastContainer = document.querySelector('.Toastify__toast-container');
              console.log('[PushNotificationProvider] ToastContainer check:', {
                exists: !!toastContainer,
                toastContainerStyles: toastContainer ? window.getComputedStyle(toastContainer as Element) : null,
              });
              
              // Check if toast is available
              if (typeof toast === 'undefined' || !toast.info) {
                console.error('[PushNotificationProvider] ❌ Toast is not available. Make sure ToastContainer is rendered in the app.');
                console.error('[PushNotificationProvider] Toast check:', {
                  toastDefined: typeof toast !== 'undefined',
                  toastInfoDefined: typeof toast !== 'undefined' && typeof toast.info !== 'undefined',
                  windowToast: (window as any).toast,
                });
                // Clean up tracking if toast is not available
                if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
                  (window as any).__pushNotificationToastIds.delete(messageId);
                }
                return;
              }
              
              console.log('[PushNotificationProvider] Attempting to display toast for notification...', {
                title,
                body,
                messageId,
                notificationType: notification.type || 'background',
                hasToast: typeof toast !== 'undefined',
                hasToastInfo: typeof toast.info !== 'undefined',
                trackingActive: (window as any).__pushNotificationToastIds?.has(messageId),
                toastContainerExists: !!document.querySelector('.Toastify__toast-container'),
              });
              
              // Display toast using simple string format (most reliable)
              const toastMessage = `${title}${body ? ': ' + body : ''}`;
              console.log('[PushNotificationProvider] Calling toast.info with message:', toastMessage);
              
              const toastId = toast.info(toastMessage, {
                position: 'top-right' as const,
                autoClose: false,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: `push-${messageId}`, // Use messageId as toastId to prevent duplicates
                onClose: () => {
                  console.log('[PushNotificationProvider] Toast closed callback triggered:', messageId);
                  // Clean up when toast is closed
                  if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
                    (window as any).__pushNotificationToastIds.delete(messageId);
                  }
                },
                onClick: () => {
                  console.log('[PushNotificationProvider] Toast clicked callback triggered, navigating to:', notificationUrl);
                  // Navigate to URL when toast is clicked
                  window.location.href = notificationUrl;
                },
              });
              
              console.log('[PushNotificationProvider] ✅ toast.info returned:', {
                messageId,
                notificationUrl,
                notificationType: notification.type || 'background',
                toastId,
                toastIdType: typeof toastId,
              });
              
              // Verify toast is in DOM immediately and after delays
              setTimeout(() => {
                const toastElement = document.querySelector(`[id*="${toastId}"]`) ||
                                    document.querySelector(`[data-toast-id*="${toastId}"]`) ||
                                    document.querySelector(`[id="${toastId}"]`) ||
                                    document.querySelector('.Toastify__toast');
                const allToasts = document.querySelectorAll('.Toastify__toast');
                console.log('[PushNotificationProvider] Toast DOM check (50ms):', {
                  toastId,
                  foundById: !!toastElement,
                  totalToasts: allToasts.length,
                  allToastIds: Array.from(allToasts).map((t: any) => t.id || t.getAttribute('data-toast-id') || 'no-id'),
                });
              }, 50);
              
              setTimeout(() => {
                const toastElement = document.querySelector(`[id*="${toastId}"]`) ||
                                    document.querySelector(`[data-toast-id*="${toastId}"]`) ||
                                    document.querySelector(`[id="${toastId}"]`) ||
                                    document.querySelector('.Toastify__toast');
                const allToasts = document.querySelectorAll('.Toastify__toast');
                if (!toastElement && allToasts.length === 0) {
                  console.error('[PushNotificationProvider] ❌ Toast was not created! Attempting fallback...');
                  // Try fallback - direct display without options
                  try {
                    const fallbackToastId = toast.info(toastMessage);
                    console.log('[PushNotificationProvider] Fallback toast displayed:', fallbackToastId);
                  } catch (fallbackError) {
                    console.error('[PushNotificationProvider] Fallback toast also failed:', fallbackError);
                  }
                } else {
                  console.log('[PushNotificationProvider] ✅ Toast verified in DOM (200ms):', {
                    toastId,
                    found: !!toastElement,
                    totalToasts: allToasts.length,
                  });
                }
              }, 200);
            } catch (toastError) {
              console.error('[PushNotificationProvider] ❌ Failed to display toast notification:', toastError);
              console.error('[PushNotificationProvider] Toast error details:', {
                error: toastError,
                errorType: typeof toastError,
                errorMessage: toastError instanceof Error ? toastError.message : String(toastError),
                stack: toastError instanceof Error ? toastError.stack : undefined,
              });
              // Clean up tracking on error
              if (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) {
                (window as any).__pushNotificationToastIds.delete(messageId);
              }
            }
          }, 150); // Small delay to ensure ToastContainer is ready
        });
        
        // Log the notification
        try {
          const { logNotification } = await import('@/lib/firebase/notificationLogger');
          // Map notification type: 'push' -> 'background', otherwise use the type or default to 'background'
          const notificationType = notification.type === 'push' ? 'background' : (notification.type === 'foreground' ? 'foreground' : 'background');
          await logNotification({
            ...notification,
            notificationType: notificationType as 'foreground' | 'background',
            receivedAt: new Date(notification.receivedAt),
          });
        } catch (error) {
          console.error('[PushNotificationProvider] Failed to log notification:', error);
        }
      }
      
      // Handle notification click logging
      if (messageEvent.data.type === 'LOG_NOTIFICATION_CLICK') {
        try {
          const { logNotificationClick } = await import('@/lib/firebase/notificationLogger');
          await logNotificationClick(messageEvent.data.notification);
        } catch (error) {
          console.error('[PushNotificationProvider] Failed to log notification click:', error);
        }
      }
    };

    // Listen for messages from service worker
    // Use 'message' event on navigator.serviceWorker (this is the correct way)
    navigator.serviceWorker.addEventListener('message', handleMessage as unknown as EventListener);
    console.log('[PushNotificationProvider] ✅ Added message listener to navigator.serviceWorker');

    // Also set up listener on the controller if it exists
    if (navigator.serviceWorker.controller) {
      console.log('[PushNotificationProvider] Service worker controller exists:', navigator.serviceWorker.controller);
      // The controller is the same as navigator.serviceWorker, so we don't need to add another listener
      // But we can verify it's there
    } else {
      console.warn('[PushNotificationProvider] ⚠️ No service worker controller found. Service worker may not be active yet.');
      
      // Wait for service worker to be ready and set up listener
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PushNotificationProvider] Service worker is ready:', registration);
        if (navigator.serviceWorker.controller) {
          console.log('[PushNotificationProvider] ✅ Service worker controller is now available');
        }
      });
    }
    
    // Also listen for when service worker becomes available
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[PushNotificationProvider] Service worker controller changed');
      if (navigator.serviceWorker.controller) {
        console.log('[PushNotificationProvider] ✅ New service worker controller available');
      }
    });

    // Process any pending notifications stored when app was closed
    const processPendingNotifications = async () => {
      try {
        const stored = localStorage.getItem('pending_notifications');
        if (stored) {
          const pendingNotifications = JSON.parse(stored);
          const { logNotification } = await import('@/lib/firebase/notificationLogger');
          
          // Log all pending notifications
          for (const notification of pendingNotifications) {
            await logNotification({
              ...notification,
              notificationType: 'background',
              receivedAt: new Date(notification.receivedAt),
            });
          }
          
          // Clear processed notifications
          localStorage.removeItem('pending_notifications');
          console.log(`[PushNotificationProvider] ✅ Processed ${pendingNotifications.length} pending notifications`);
        }
      } catch (error) {
        console.error('[PushNotificationProvider] Failed to process pending notifications:', error);
      }
    };

    // Process pending notifications after a short delay to ensure auth is ready
    setTimeout(processPendingNotifications, 1000);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage as unknown as EventListener);
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.removeEventListener('message', handleMessage as unknown as EventListener);
      }
    };
  }, []);

  return <>{children}</>;
}

